import React, { useEffect, useState } from "react";
import { useNavigate, Link as RouterLink, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Chip,
  Avatar,
  TextField,
  MenuItem,
  Grid,
  IconButton,
  useTheme,
  CircularProgress,
  Tooltip,
  Divider,
  Paper,
  Snackbar,
  Alert
} from "@mui/material";
import { 
  Add, 
  FiberManualRecord, 
  Delete, 
  Edit,
  Chat as ChatIcon,
  InfoOutlined
} from "@mui/icons-material";
import chatsApi from "../api/chatsApi";
import knowledgeApi from "../api/knowledgeApi";

const Chats = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [chats, setChats] = useState([]);
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [newChat, setNewChat] = useState({
    name: "",
    description: "",
    knowledgeBaseId: "",
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [chatsData, knowledgeBasesData] = await Promise.all([
          chatsApi.getAll(),
          knowledgeApi.getAll(),
        ]);
        
        setChats(chatsData || []);
        setKnowledgeBases(knowledgeBasesData || []);
      } catch (error) {
        console.error("Loading error:", error);
        setError("Failed to load chats and knowledge bases");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleCreateChat = async () => {
    try {
      if (!newChat.name.trim()) {
        setError("Chat name is required");
        return;
      }
      
      const createdChat = await chatsApi.create({
        name: newChat.name,
        description: newChat.description,
        knowledgeBaseId: newChat.knowledgeBaseId,
      });
      
      setChats(prevChats => [...prevChats, createdChat]);
      navigate(`/dashboard/chats/${createdChat.id}`);
    } catch (error) {
      console.error("Chat creation failed:", error);
      setError("Failed to create chat");
    }
  };

  const handleDeleteChat = async (chatId, e) => {
    e.stopPropagation();
    try {
      await chatsApi.delete(chatId);
      setChats(chats.filter((chat) => chat.id !== chatId));
    } catch (error) {
      console.error("Error deleting chat:", error);
      setError("Failed to delete chat");
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        height: 'calc(100vh - 200px)'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      width="100%"
      height="calc(100vh - 100px)"
      display="flex"
      flexDirection="column"
      sx={{
        backgroundColor: theme.palette.background.default,
      }}
    >
      {/* Fixed Header Section */}
      <Box sx={{ 
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        p: 2,
        backgroundColor: theme.palette.background.default,
        zIndex: 1,
        position: 'sticky',
        top: 0
      }}>
        <Typography variant="h5" sx={{ 
          fontWeight: 600,
          color: theme.palette.text.primary
        }}>
          My Chats
        </Typography>
        
        <Button
          component={RouterLink}
          to="/dashboard/chats/create"
          variant="contained"
          startIcon={<Add />}
          sx={{
            borderRadius: 1,
            px: 2,
            py: 1,
            textTransform: 'none',
            fontWeight: 500
          }}
        >
          New Chat
        </Button>
      </Box>

      {/* Fixed Divider */}
      <Divider sx={{ width: '100%' }} />

      {/* Scrollable Content Area */}
      <Box sx={{ 
        flex: 1,
        overflow: 'auto',
        p: 2
      }}>
        {/* Create Chat Form */}
        {location.pathname === "/dashboard/chats/create" && (
          <Paper sx={{ 
            p: 3, 
            mb: 3,
            borderRadius: 2,
            boxShadow: theme.shadows[1]
          }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
              Create New Chat
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Chat Name"
                  value={newChat.name}
                  onChange={(e) => setNewChat({ ...newChat, name: e.target.value })}
                  sx={{ mb: 2 }}
                  variant="outlined"
                  required
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Knowledge Base"
                  value={newChat.knowledgeBaseId}
                  onChange={(e) => setNewChat({ ...newChat, knowledgeBaseId: e.target.value })}
                  sx={{ mb: 2 }}
                  variant="outlined"
                >
                  <MenuItem value="">None</MenuItem>
                  {knowledgeBases.map((kb) => (
                    <MenuItem key={kb.id} value={kb.id}>
                      {kb.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={newChat.description}
                  onChange={(e) => setNewChat({ ...newChat, description: e.target.value })}
                  sx={{ mb: 2 }}
                  variant="outlined"
                />
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleCreateChat}
                    startIcon={<Add />}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 500
                    }}
                  >
                    Create Chat
                  </Button>
                  
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/dashboard/chats')}
                    sx={{
                      textTransform: 'none'
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Chat List */}
        {!location.pathname.includes('/create') && (
          <>
            {chats.length === 0 ? (
              <Paper sx={{ 
                p: 4, 
                textAlign: 'center',
                borderRadius: 2
              }}>
                <ChatIcon sx={{ 
                  fontSize: 60,
                  color: theme.palette.text.disabled,
                  mb: 2
                }} />
                
                <Typography variant="h6" sx={{ mb: 1 }}>
                  No chats yet
                </Typography>
                
                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                  Start a new conversation by creating your first chat
                </Typography>
                
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => navigate("/dashboard/chats/create")}
                  sx={{
                    borderRadius: 1,
                    px: 3,
                    py: 1,
                    textTransform: 'none'
                  }}
                >
                  Create Chat
                </Button>
              </Paper>
            ) : (
              <Grid container spacing={2}>
                {chats.map((chat) => {
                  const knowledgeBase = knowledgeBases.find(
                    (kb) => String(kb.id) === String(chat.knowledgeBaseId)
                  );
                  
                  return (
                    <Grid item xs={12} sm={6} md={4} key={chat.id}>
                      <Paper
                        sx={{
                          p: 2,
                          height: '100%',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            boxShadow: theme.shadows[3],
                            borderColor: theme.palette.primary.light
                          },
                          border: '1px solid',
                          borderColor: theme.palette.divider,
                          borderRadius: 2,
                          position: 'relative'
                        }}
                        onClick={() => navigate(`/dashboard/chats/${chat.id}`)}
                      >
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          mb: 1.5
                        }}>
                          <Avatar sx={{ 
                            bgcolor: theme.palette.primary.main,
                            color: theme.palette.common.white,
                            width: 40,
                            height: 40,
                            mr: 1.5
                          }}>
                            <ChatIcon />
                          </Avatar>
                          
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" sx={{ 
                              fontWeight: 500,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {chat.name}
                            </Typography>
                            
                            <Chip
                              label="Active"
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.7rem',
                                bgcolor: theme.palette.success.light,
                                color: theme.palette.success.dark
                              }}
                              icon={<FiberManualRecord sx={{ fontSize: '0.7rem' }} />}
                            />
                          </Box>
                        </Box>
                        
                        <Typography variant="body2" color="textSecondary" sx={{ 
                          mb: 2,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}>
                          {chat.description || 'No description'}
                        </Typography>
                        
                        {knowledgeBase && (
                          <Box sx={{ 
                            display: 'flex',
                            alignItems: 'center',
                            mb: 2
                          }}>
                            <InfoOutlined sx={{ 
                              fontSize: '1rem',
                              mr: 0.5,
                              color: theme.palette.text.secondary
                            }} />
                            
                            <Typography variant="caption" sx={{
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              Linked to: {knowledgeBase.name}
                            </Typography>
                          </Box>
                        )}
                        
                        <Box sx={{ 
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mt: 'auto'
                        }}>
                          <Typography variant="caption" color="textSecondary">
                            {new Date(chat.createdAt).toLocaleDateString()}
                          </Typography>
                          
                          <Box>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Handle edit functionality
                                }}
                                sx={{
                                  color: theme.palette.text.secondary,
                                  '&:hover': {
                                    color: theme.palette.primary.main
                                  }
                                }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={(e) => handleDeleteChat(chat.id, e)}
                                sx={{
                                  color: theme.palette.text.secondary,
                                  '&:hover': {
                                    color: theme.palette.error.main
                                  }
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </>
        )}
      </Box>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          severity="error"
          sx={{ width: '100%' }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Chats;